// src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AllJobsPage from './components/Jobs/AllJobsPage';
import Auth from './components/Auth/Auth';
import SavedJobsPage from './components/Jobs/SavedJobsPage';
import Account from './components/Profile/Account';
import ResetPasswordConfirm from './components/Auth/ResetPasswordConfirm';
import PrivacyPolicy from './components/Legal/PrivacyPolicy';
import Terms from './components/Legal/Terms';
import About from './components/Legal/About';
import Layout from './components/Layout/Layout';
import Navbar from './components/Navigation/Navbar';
import { AuthProvider, useAuth } from './contexts/AuthContext';

/**
 * Protected route component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render when authenticated
 * @returns {JSX.Element} Protected route or redirect
 */
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

/**
 * Main application content component
 * @returns {JSX.Element} Application content
 */
function AppContent() {
  return (
    <Router>
      <Layout>
        <Navbar />
        <Routes>
          <Route path="/" element={<AllJobsPage />} />
          <Route path="/login" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPasswordConfirm />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          {/* Primary route for saved jobs */}
          <Route 
            path="/saved" 
            element={
              <ProtectedRoute>
                <SavedJobsPage />
              </ProtectedRoute>
            } 
          />
          {/* Redirect old /profile URL to /saved */}
          <Route 
            path="/profile" 
            element={<Navigate to="/saved" replace />} 
          />
          <Route 
            path="/account" 
            element={
              <ProtectedRoute>
                <Account />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </Layout>
    </Router>
  );
}

/**
 * Main App component with AuthProvider
 * @returns {JSX.Element} Main App component
 */
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;